import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Filter, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { successStories } from '../data/mock';
import { SEO } from './SEO';
import { buildBreadcrumb } from '../lib/seo-schemas';

export const ReviewsPage = () => {
  const navigate = useNavigate();
  const [filterRating, setFilterRating] = useState('all');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    program: '',
    college: '',
    country: '',
    rating: 5,
    review: ''
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredReviews = filterRating === 'all' 
    ? successStories 
    : successStories.filter(story => story.rating === parseInt(filterRating));

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Thank you for your review!', {
      description: 'Your review has been submitted and will be published after verification.',
    });
    setFormData({
      name: '',
      program: '',
      college: '',
      country: '',
      rating: 5,
      review: ''
    });
    setShowReviewForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const averageRating = (successStories.reduce((acc, story) => acc + story.rating, 0) / successStories.length).toFixed(1);

  const reviewsSchema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    '@id': 'https://elevatededucation.ca/#organization',
    name: 'Elevated Education',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: averageRating,
      reviewCount: successStories.length,
      bestRating: '5',
      worstRating: '1',
    },
    review: successStories.map((s) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: s.name },
      datePublished: s.date,
      reviewBody: s.testimonial,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: String(s.rating),
        bestRating: '5',
      },
    })),
  };

  return (
    <div className="min-h-screen pt-32 pb-16 bg-gray-50">
      <SEO
        title="Student Reviews – Real Experiences with ElevatED"
        description={`Read ${successStories.length} verified reviews from students who applied to BC career colleges with ElevatED. Average rating ${averageRating}/5.`}
        path="/reviews"
        schema={[
          reviewsSchema,
          buildBreadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Reviews', path: '/reviews' },
          ]),
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/#testimonials"
          className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-8 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Student Reviews
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Read genuine reviews from students who achieved their educational goals with ElevatED
          </p>
          
          {/* Rating Summary */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="flex items-center">
              <Star className="w-8 h-8 text-yellow-400 fill-current" />
              <span className="text-4xl font-bold text-gray-900 ml-2">{averageRating}</span>
            </div>
            <div className="text-left">
              <p className="text-gray-600">Average Rating</p>
              <p className="text-sm text-gray-500">Based on {successStories.length} reviews</p>
            </div>
          </div>

          <Button 
            onClick={() => setShowReviewForm(!showReviewForm)}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
            size="lg"
          >
            {showReviewForm ? 'Close Form' : 'Leave a Review'}
          </Button>
        </div>

        {/* Review Form */}
        {showReviewForm && (
          <div className="max-w-2xl mx-auto mb-12 bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Share Your Experience</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <Input
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="e.g., India, Mexico"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Program
                  </label>
                  <Input
                    name="program"
                    value={formData.program}
                    onChange={handleChange}
                    placeholder="e.g., Health Care Assistant"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    College
                  </label>
                  <select
                    name="college"
                    value={formData.college}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select College</option>
                    <option value="Granville College">Granville College</option>
                    <option value="Cambria College">Cambria College</option>
                    <option value="VCCT">VCCT</option>
                    <option value="ACC">Assiniboine Community College (ACC)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating *
                </label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, rating }))}
                      className="focus:outline-none"
                    >
                      <Star 
                        className={`w-8 h-8 ${
                          rating <= formData.rating 
                            ? 'text-yellow-400 fill-current' 
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review *
                </label>
                <Textarea
                  name="review"
                  required
                  value={formData.review}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Share your experience with ElevatED..."
                />
              </div>

              <Button type="submit" className="w-full" size="lg">
                <Send className="mr-2 w-5 h-5" />
                Submit Review
              </Button>
            </form>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-600">
            Showing <span className="font-semibold">{filteredReviews.length}</span> review{filteredReviews.length !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
            </select>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReviews.map((story) => (
            <div 
              key={story.id}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold flex-shrink-0">
                    {story.initials}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{story.name}</h3>
                    <p className="text-xs text-gray-500">From {story.country}</p>
                  </div>
                </div>
                <div className="flex">
                  {[...Array(story.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>

              {/* Review Text */}
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                "{story.testimonial}"
              </p>

              {/* Program Info */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">{story.program}</p>
                {story.date && (
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(story.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-12 text-white text-center shadow-xl">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join hundreds of successful students who achieved their educational goals with ElevatED.
          </p>
          <Button 
            onClick={() => navigate('/#contact')}
            className="bg-white text-blue-600 hover:bg-gray-100"
            size="lg"
          >
            Book Free Consultation
          </Button>
        </div>
      </div>
    </div>
  );
};
